// Shared UI components used across screens and games
import { useEffect, useRef, useState } from "react";
import { speakHe, speakEn } from "../utils/speech";
import { useGameSession } from "../store/GameSessionContext";

export function Confetti({ active }) {
  if (!active) return null;
  const colors = ["#FF6B6B","#4ECDC4","#FFD700","#A78BFA","#48BB78","#F6AD55","#FC8181"];
  return (
    <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:9999,overflow:"hidden"}}>
      {Array.from({length:36},(_,i) => (
        <div key={i} style={{
          position:"absolute", left:`${5+Math.random()*90}%`, top:"-20px",
          width:i%4===0?14:i%3===0?10:7, height:i%4===0?14:i%3===0?10:7,
          background:colors[i%colors.length], borderRadius:i%2===0?"50%":"3px",
          animation:`fall ${1.2+Math.random()*1.2}s linear ${Math.random()*0.8}s forwards`,
        }}/>
      ))}
      <style>{`@keyframes fall{to{transform:translateY(110vh) rotate(720deg);opacity:0}}`}</style>
    </div>
  );
}

export function ProgressBar({ current, total, color }) {
  return (
    <div style={{background:"rgba(0,0,0,0.08)",borderRadius:99,height:10,overflow:"hidden"}}>
      <div style={{
        width:`${(current/total)*100}%`, height:"100%", background:color, borderRadius:99,
        transition:"width 0.5s cubic-bezier(.4,2,.6,1)", boxShadow:`0 0 10px ${color}88`,
      }}/>
    </div>
  );
}

export function StarRow({ count, size=22 }) {
  return (
    <div style={{display:"flex",gap:2,justifyContent:"center"}}>
      {[1,2,3].map(i => (
        <span key={i} style={{
          fontSize:size,
          filter:i<=count?"none":"grayscale(1) opacity(0.25)",
          transition:"all 0.4s", transitionDelay:`${i*0.1}s`,
        }}>⭐</span>
      ))}
    </div>
  );
}

// ── EncouragementBubble ────────────────────────────────────────────────────────
// Shows a cheerful popup above the answer for 800ms after correct/wrong answer.
// Pass `visible` to mount/unmount, `isCorrect` to control message tone.
const CORRECT_MSGS = ["כל הכבוד! 🌟","מדהים! 🎉","יופי! ✨","נכון! 👏","סופר! 🔥","וואו! 🤩","מצוין! 💯"];
let _msgIndex = 0;

export function EncouragementBubble({ visible, isCorrect }) {
  if (!visible) return null;
  const msg = isCorrect ? CORRECT_MSGS[_msgIndex++ % CORRECT_MSGS.length] : "נסה שוב 💪";
  return (
    <div style={{
      position:"fixed", top:"18%", left:"50%", transform:"translateX(-50%)",
      zIndex:8888, pointerEvents:"none",
      background:"white",
      color: isCorrect ? "#2d6a4f" : "#c05621",
      fontFamily:"'Fredoka One', 'Rubik', cursive",
      fontWeight:700, fontSize:22,
      padding:"10px 28px", borderRadius:99,
      boxShadow: isCorrect
        ? "0 8px 32px rgba(72,187,120,0.45)"
        : "0 8px 32px rgba(252,129,129,0.4)",
      border: isCorrect ? "2px solid #9ae6b4" : "2px solid #feb2b2",
      animation:"encouragePop 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
      whiteSpace:"nowrap",
    }}>
      {msg}
      <style>{`@keyframes encouragePop{from{transform:translateX(-50%) scale(0.4);opacity:0}to{transform:translateX(-50%) scale(1);opacity:1}}`}</style>
    </div>
  );
}

// ── AnswerButton ───────────────────────────────────────────────────────────────
// Standardised answer button for all games.
// variant: "emoji" | "word" | "number" | "letter"
// state:   "idle" | "correct" | "wrong"
export function AnswerButton({ children, variant = "word", state = "idle", onClick, primaryColor = "#4ECDC4", disabled = false, style: extraStyle = {} }) {
  const isCorrect = state === "correct";
  const isWrong   = state === "wrong";

  const baseFontSize = variant === "emoji" ? 44 : variant === "letter" ? 52 : variant === "number" ? 40 : 20;

  const bgColor   = isCorrect ? "#F0FFF4" : isWrong ? "#FFF5F5" : "white";
  const borderCol = isCorrect ? "#48BB78" : isWrong ? "#FC8181" : `${primaryColor}55`;
  const scale     = isCorrect ? 1.08 : isWrong ? 0.93 : 1;
  const shadow    = isCorrect
    ? "0 0 24px rgba(72,187,120,0.5)"
    : isWrong ? "none"
    : `0 4px 16px ${primaryColor}18`;

  return (
    <button
      onClick={disabled ? undefined : onClick}
      style={{
        position:"relative",
        minHeight: 80,
        width:"100%",
        borderRadius: 20,
        border: `3px solid ${borderCol}`,
        background: bgColor,
        cursor: disabled ? "default" : "pointer",
        padding: "16px 12px",
        fontSize: baseFontSize,
        fontWeight: variant === "word" ? 800 : 900,
        fontFamily: variant === "letter" || variant === "emoji" ? "'Fredoka One','Rubik',cursive" : "'Rubik','Segoe UI',sans-serif",
        color: isCorrect ? "#276749" : isWrong ? "#c53030" : "#2d3748",
        transform: `scale(${scale})`,
        transition: "all 0.18s ease",
        boxShadow: shadow,
        display: "flex", alignItems: "center", justifyContent: "center",
        lineHeight: 1.2,
        ...extraStyle,
      }}
      onMouseEnter={e => { if (!disabled && state === "idle") { e.currentTarget.style.transform = "scale(1.04)"; e.currentTarget.style.boxShadow = `0 6px 22px ${primaryColor}30`; } }}
      onMouseLeave={e => { if (!disabled && state === "idle") { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = shadow; } }}
    >
      {children}
      {isCorrect && (
        <span style={{
          position:"absolute", top:6, left:6,
          width:22, height:22, borderRadius:"50%",
          background:"#48BB78", color:"white",
          fontSize:13, fontWeight:900,
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>✓</span>
      )}
    </button>
  );
}

// ── GameHeader ─────────────────────────────────────────────────────────────────
// streak: number — show 🔥 badge when >= 3
// instruction: string — spoken aloud once on mount and repeatable via the 🔊
//              button (critical for pre-readers who can't read the prompt).
// lang: "he" | "en" — language of the instruction (default Hebrew)
// 🔀 "new questions" appears automatically inside a game (GameSessionContext);
// games don't pass anything for it.

// How long the 🔀 button stays armed waiting for the confirming second tap.
const NEW_SET_ARM_MS = 3000;

export function GameHeader({ title, emoji, color, score, round, total, onBack, streak = 0, instruction, lang = "he" }) {
  const spokenRef = useRef(false);
  const speakInstruction = () => {
    if (!instruction) return;
    (lang === "en" ? speakEn : speakHe)(instruction);
  };
  useEffect(() => {
    if (!instruction || spokenRef.current) return;
    spokenRef.current = true;
    const t = setTimeout(speakInstruction, 450); // let the screen settle first
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔀 "new questions" — remounts the game with a freshly drawn set.
  // Two taps to fire: pre-readers can't be shown a confirm dialog, and a
  // curious 4-year-old would otherwise wipe their round with one stray tap.
  // The first tap arms (amber ✅ + spoken question), the second within
  // NEW_SET_ARM_MS commits, and otherwise it quietly disarms.
  const { newSet } = useGameSession();
  const [armed, setArmed] = useState(false);
  const armTimer = useRef(null);
  useEffect(() => () => clearTimeout(armTimer.current), []);
  const tapNewSet = () => {
    if (armed) {
      clearTimeout(armTimer.current);
      setArmed(false);
      newSet();
      return;
    }
    setArmed(true);
    speakHe("שאלות חדשות?");
    armTimer.current = setTimeout(() => setArmed(false), NEW_SET_ARM_MS);
  };

  return (
    <div style={{width:"100%",maxWidth:440,marginBottom:16}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
        {/* RTL: "back" points right, like every Hebrew app */}
        <button onClick={onBack} aria-label="חזרה" style={{
          width:42,height:42,borderRadius:14,border:"none",
          background:"rgba(255,255,255,0.9)",fontSize:20,cursor:"pointer",
          boxShadow:"0 2px 10px rgba(0,0,0,0.12)",flexShrink:0,
        }}>→</button>
        {instruction && (
          <button onClick={speakInstruction} aria-label="השמע הוראות" style={{
            width:42,height:42,borderRadius:14,border:"none",
            background:"rgba(255,255,255,0.9)",fontSize:18,cursor:"pointer",
            boxShadow:"0 2px 10px rgba(0,0,0,0.12)",flexShrink:0,
          }}>🔊</button>
        )}
        {newSet && (
          <button onClick={tapNewSet} aria-label={armed ? "אישור שאלות חדשות" : "שאלות חדשות"} style={{
            width:42,height:42,borderRadius:14,border:"none",
            background: armed ? "linear-gradient(135deg,#F6AD55,#DD6B20)" : "rgba(255,255,255,0.9)",
            fontSize:18,cursor:"pointer",
            boxShadow: armed ? "0 3px 14px rgba(221,107,32,0.5)" : "0 2px 10px rgba(0,0,0,0.12)",
            flexShrink:0,
            animation: armed ? "armPulse 0.7s ease-in-out infinite alternate" : "none",
          }}>{armed ? "✅" : "🔀"}</button>
        )}
        {/* A flex child will not shrink below its own text without minWidth:0.
            Three 42px buttons, the score badge and the 🔥 streak badge leave
            about 96px here on a 390px phone, which every game title exceeds —
            without this the row grew and the header wrapped to two lines
            exactly when the child was on a streak. */}
        <div style={{
          flex:1, minWidth:0, textAlign:"center", fontWeight:900, fontSize:16,
          color:"#2d3748", fontFamily:"'Rubik','Segoe UI',sans-serif",
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
        }}>{emoji} {title}</div>
        <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
          {streak >= 3 && (
            <div style={{
              background:"linear-gradient(135deg,#F6AD55,#E53E3E)",
              color:"white", borderRadius:14, padding:"4px 10px",
              fontWeight:900, fontSize:13,
              boxShadow:"0 3px 10px rgba(229,62,62,0.45)",
              animation:"streakPulse 0.8s ease-in-out infinite alternate",
            }}>🔥{streak}</div>
          )}
          <div style={{
            background:color,color:"white",borderRadius:14,padding:"5px 14px",
            fontWeight:900,fontSize:15,boxShadow:`0 4px 14px ${color}77`,
          }}>⭐{score}</div>
        </div>
      </div>
      {total != null && <>
        <ProgressBar current={round} total={total} color={color}/>
        <div style={{textAlign:"center",fontSize:12,color:"#4a5568",marginTop:3}}>
          שאלה {round+1} מתוך {total}
        </div>
      </>}
      <style>{`
        @keyframes streakPulse{from{box-shadow:0 3px 10px rgba(229,62,62,0.45)}to{box-shadow:0 3px 18px rgba(229,62,62,0.75)}}
        /* Pulse the glow, not the box — a scaling button is a moving tap target for a 4-year-old. */
        @keyframes armPulse{from{box-shadow:0 3px 12px rgba(221,107,32,0.45)}to{box-shadow:0 3px 20px rgba(221,107,32,0.85)}}
      `}</style>
    </div>
  );
}

export function KidAvatar({ kid, size = 56, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        width: size, height: size, borderRadius: "50%",
        background: `linear-gradient(135deg, ${kid.color}33, ${kid.color}88)`,
        border: `3px solid ${kid.color}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.48, cursor: onClick ? "pointer" : "default",
        boxShadow: `0 4px 16px ${kid.color}44`,
      }}
    >
      {kid.avatar}
    </div>
  );
}

export function Spinner() {
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
      <div style={{
        width:40,height:40,borderRadius:"50%",
        border:"4px solid #e2e8f0",borderTopColor:"#4ECDC4",
        animation:"spin 0.8s linear infinite",
      }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
